import { useCallback, useEffect, useState } from 'react';
import { useDashboard } from '../../context/dashboardContext';
import { LoadingModal } from '../../components/shareable/loadingModal';
import CustomerPagination from '../../components/shareable/customerPagination';
import { useSearch } from '../../context/SearchContext'; // Import search context

const ProductSaw = () => {
  const { getSawProducts } = useDashboard();
  const { searchQuery, updateSearch } = useSearch(); // Get search context
  const [allSaws, setAllSaws] = useState([]); // Store all saw products
  const [filteredSaws, setFilteredSaws] = useState([]); // Store filtered saw products
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Set the page in search context when component mounts
  useEffect(() => {
    updateSearch('', 'productsaw');
    
    // Clear search when component unmounts
    return () => updateSearch('', '');
  }, [updateSearch]);

  // Filter saw products based on search query
  const filterSaws = useCallback((saws, query) => {
    if (!query.trim()) return saws;
    
    const searchTerm = query.toLowerCase().trim();
    return saws.filter(saw => {
      // Safely check each field with optional chaining and nullish coalescing
      return (
        (saw.brand?.toLowerCase() || '').includes(searchTerm) ||
        (saw.model?.toLowerCase() || '').includes(searchTerm)
      );
    });
  }, []);

  const fetchData = useCallback(async () => {
    const saw = await getSawProducts();
    if (saw.success) {
      setAllSaws(saw.data);
      
      // Apply search filter if there's a query
      const filtered = filterSaws(saw.data, searchQuery);
      setFilteredSaws(filtered);
    }
    setLoading(false);
  }, [getSawProducts, searchQuery, filterSaws]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply search filter when search query changes
  useEffect(() => {
    if (allSaws.length > 0) {
      const filtered = filterSaws(allSaws, searchQuery);
      setFilteredSaws(filtered);
      setCurrentPage(1); // Reset to first page when search changes
    }
  }, [searchQuery, allSaws, filterSaws]);

  // Pagination logic - use filteredSaws instead of all saws
  const totalPages = Math.ceil(filteredSaws.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSaws = filteredSaws.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return loading ? (
    <LoadingModal loading={loading} />
  ) : (
    <div className="p-6 w-full">
      <div className="relative mb-3 mt-3">
        <h2 className="text-2xl font-bold text-red-700 uppercase tracking-wide">
          Saw Products {searchQuery && `(Filtered: ${filteredSaws.length} found)`}
        </h2>
      </div>

      <div className="w-full overflow-x-auto">
        {currentSaws?.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Index
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Brand
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bed Height
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Datum Side
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Saw Type
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentSaws.map((s, i) => (
                <tr key={s._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {indexOfFirstItem + i + 1}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {s.brand || <span className="italic text-gray-400">N/A</span>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {s.model || <span className="italic text-gray-400">N/A</span>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {s.work_bed_height || <span className="italic text-gray-400">N/A</span>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {s.datum_side || <span className="italic text-gray-400">N/A</span>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {s.price || <span className="italic text-gray-400">N/A</span>}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900">
                    {s.description || <span className="italic text-gray-400">N/A</span>}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                    {s.saw_type || <span className="italic text-gray-400">N/A</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : (
          <div className="w-full px-6 py-8 text-center text-sm text-gray-500 border border-gray-200 rounded-lg">
            <div className="flex flex-col items-center justify-center">
              <svg
                className="w-12 h-12 text-gray-400 mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {searchQuery ? 'No saw products match your search' : 'No saw products available'}
            </div>
          </div>
        )}
      </div>

      {filteredSaws.length > 0 && (
        <div className="mt-4">
          <CustomerPagination
            totalPages={totalPages}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            totalItems={filteredSaws.length}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}
    </div>
  );
};

export default ProductSaw;
